import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

const ROLE_LABELS = {
  admin: 'Admin',
  site_manager: 'Site Manager',
  worker: 'Worker',
  client: 'Client',
};

const UserProfileDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user, userProfile, role, signOut } = useAuth();
  const { t } = useLanguage();

  const userRoleLabel = ROLE_LABELS?.[role] || role || 'Unknown';
  const userName = userProfile?.full_name || userProfile?.name || user?.email?.split('@')?.[0] || 'User';
  const userEmail = user?.email || userProfile?.email || '';
  const companyName = userProfile?.company_name || '';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    setIsOpen(false);
    await signOut();
    navigate('/login');
  };

  const menuItems = [
    {
      label: t('profileSettings'),
      icon: 'User',
      action: () => {
        setIsOpen(false);
        navigate('/profile-settings');
      }
    },
    {
      label: t('companySettings'),
      icon: 'Building2',
      action: () => {
        setIsOpen(false);
        navigate('/company-settings');
      }
    },
    {
      label: t('helpSupport'),
      icon: 'HelpCircle',
      action: () => {
        setIsOpen(false);
        navigate('/help');
      }
    }
  ];

  const initials = userName?.split(' ')?.map(n => n?.[0])?.join('') || '?';

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="rounded-full"
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        <div className="flex items-center justify-center w-10 h-10 bg-accent text-accent-foreground rounded-full font-medium">
          {initials}
        </div>
      </Button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-popover border border-border rounded-lg shadow-elevation-4 z-dropdown">
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-accent text-accent-foreground rounded-full font-medium text-lg">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">{userName}</p>
                <p className="text-sm text-muted-foreground truncate">{userEmail}</p>
              </div>
            </div>
            <div className="mt-3 px-3 py-2 bg-muted rounded-md">
              <p className="text-xs text-muted-foreground font-caption">{t('userMenuRole')}</p>
              <p className="text-sm font-medium text-foreground">{userRoleLabel}</p>
              {companyName && <p className="text-xs text-muted-foreground mt-1">{companyName}</p>}
            </div>
          </div>

          <div className="py-2">
            {menuItems?.map((item, index) => (
              <button
                key={index}
                onClick={item?.action}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-foreground hover:bg-muted transition-smooth touch-target cursor-pointer"
              >
                <Icon name={item?.icon} size={20} />
                <span className="font-medium">{item?.label}</span>
              </button>
            ))}
          </div>

          <div className="p-2 border-t border-border">
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Icon name="LogOut" size={20} className="mr-3" />
              <span className="font-medium">{t('signOut')}</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;